import { Injectable, Injector } from '@angular/core';
import { AtlasConfigService } from '@galaxy/atlas/core';
import { BusinessNavConfigService } from '@vendasta/business-nav';
import { BehaviorSubject, distinctUntilChanged, map, of, shareReplay, switchMap } from 'rxjs';
import { partnerId } from '../../globals';
import { AccountGroup, AccountGroupService } from '../account-group';
import { AccountGroupCacheService } from '../brands/account-group-cache.service';
import { Brand } from '../brands/brand';
import { BrandsApiService } from '../brands/brands.api.service';

export type Location = AccountGroup | Brand | null;

export function isAccountGroup(locationRef: AccountGroup | Brand): locationRef is AccountGroup {
  return locationRef instanceof AccountGroup;
}

export function isBrand(locationRef: AccountGroup | Brand): locationRef is Brand {
  return locationRef instanceof Brand;
}

@Injectable({ providedIn: 'root' })
export class LocationsService {
  private currentLocation$$ = new BehaviorSubject<Location>(null);
  readonly currentLocation$ = this.currentLocation$$.asObservable();

  readonly marketId$ = this.currentLocation$.pipe(
    map((loc) => {
      if (!loc) {
        return '';
      }
      return loc.marketId;
    }),
    distinctUntilChanged(),
    shareReplay(1),
  );

  readonly isCurrentLocationABrand$ = this.currentLocation$.pipe(map((location) => isBrand(location)));

  readonly currentAccountGroups$ = this.currentLocation$.pipe(
    switchMap((location) => {
      if (isBrand(location)) {
        const path = location.groupNodes;
        return this.brandsApiService.listLocationIDs(path.join('|')).pipe(
          switchMap((accountGroupIds) => {
            const accountGroupCacheService = this.injector.get(AccountGroupCacheService);
            return accountGroupCacheService.getMulti(accountGroupIds);
          }),
        );
      }
      if (isAccountGroup(location)) {
        return of({
          [location.accountGroupId]: location,
        });
      }
      return of({});
    }),
    shareReplay(1),
  );

  readonly currentAccountGroupIds$ = this.currentAccountGroups$.pipe(
    map((accountGroups) => Object.keys(accountGroups)),
  );

  constructor(
    private atlasConfig: AtlasConfigService,
    private businessNavConfig: BusinessNavConfigService,
    private brandsApiService: BrandsApiService,
    private injector: Injector,
    private accountGroupService: AccountGroupService,
  ) {
    this.currentLocation$.subscribe((location) => {
      if (isBrand(location)) {
        this.atlasConfig.initialize('VBC', partnerId, location.marketId, null, null, location.groupNodes[0], '');
        this.businessNavConfig.initialize(partnerId, location.marketId, null, location.groupNodes[0], 'VBC', null);
      } else if (isAccountGroup(location)) {
        this.atlasConfig.initialize('VBC', partnerId, location.marketId, location.accountGroupId, null, null, '');
        this.businessNavConfig.initialize(partnerId, location.marketId, location.accountGroupId, null, 'VBC', null);
        this.accountGroupService.switchAccountGroup(location);
      } else {
        this.atlasConfig.initialize('VBC', partnerId, null, null, null, null, '');
        this.businessNavConfig.initialize(partnerId, null, null, null, 'VBC', null);
      }
    });
  }

  setCurrentLocation(location: Location): void {
    this.currentLocation$$.next(location);
  }
}
